import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaUser, FaEnvelope, FaLock, FaCamera, FaEdit, FaCheck, FaTimes } from 'react-icons/fa';
import { getUserProfile, updateUserProfile } from '../redux/Slices/authSlice';
import { useTitle } from '../context/TitleContext';
import { toast } from "react-toastify";

const Profile = () => {
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const { profile, isLoading: loading, error, success } = useSelector((state) => state.auth);

  // Profile form states
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    image: null,
    password: ''
  });

  // Original profile data for comparison
  const [originalProfileData, setOriginalProfileData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setTitle('الملف الشخصي');
    dispatch(getUserProfile());
  }, [dispatch, setTitle]);

  useEffect(() => {
    if (profile) {
      const initialData = {
        name: profile.name || '',
        email: profile.email || '',
        image: null,
        password: ''
      };
      setProfileData(initialData);
      setOriginalProfileData({
        name: profile.name || '',
        email: profile.email || '',
        password: ''
      });
      setImagePreview(profile.image);
    }
  }, [profile]);

  // Check for changes
  useEffect(() => {
    const nameChanged = profileData.name !== originalProfileData.name;
    const emailChanged = profileData.email !== originalProfileData.email;
    const passwordChanged = profileData.password.trim() !== '';
    const imageChanged = profileData.image !== null;
    setHasChanges(nameChanged || emailChanged || passwordChanged || imageChanged);
  }, [profileData, originalProfileData]);

  useEffect(() => {
    if (success) {
      toast.success(success, {
        position: "top-right",
        rtl: true,
        autoClose: 1000,
        onClose: () => {
          setIsEditing(false);
        }
      });
    }
    if (error) {
      toast.error(error, {
        position: "top-right",
        rtl: true
      });
    }
  }, [success, error]);

  const handleProfileChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'image' && files[0]) {
      const file = files[0];
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      const maxSize = 2 * 1024 * 1024; // 2MB

      if (!validTypes.includes(file.type)) {
        toast.error('يرجى اختيار صورة بصيغة صحيحة (JPG, PNG, WEBP)', {
          position: "top-right",
          rtl: true
        });
        return;
      }

      if (file.size > maxSize) {
        toast.error('حجم الصورة يجب أن يكون أقل من 2 ميجا بايت', {
          position: "top-right",
          rtl: true
        });
        return;
      }

      setProfileData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setProfileData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    if (!profileData.name.trim()) {
      toast.error('الاسم مطلوب', { position: "top-right", rtl: true });
      return;
    }

    if (!profileData.email.trim()) {
      toast.error('البريد الإلكتروني مطلوب', { position: "top-right", rtl: true });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profileData.email)) {
      toast.error('صيغة البريد الإلكتروني غير صحيحة', { position: "top-right", rtl: true });
      return;
    }

    if (profileData.password.trim() && profileData.password.length < 8) {
      toast.error('كلمة المرور يجب أن تكون 8 أحرف على الأقل', { position: "top-right", rtl: true });
      return;
    }

    const hasChanges =
      profileData.name !== originalProfileData.name ||
      profileData.email !== originalProfileData.email ||
      profileData.password.trim() !== "" ||
      profileData.image !== null;

    if (!hasChanges) {
      toast.info('لا توجد تغييرات لحفظها', { position: "top-right", rtl: true });
      return;
    }

    const formData = new FormData();
    formData.append('name', profileData.name);
    formData.append('email', profileData.email);
    if (profileData.password.trim() !== '') {
      formData.append('password', profileData.password);
    }
    if (profileData.image !== null) {
      formData.append('image', profileData.image);
    }

    dispatch(updateUserProfile(formData));
  };

  const handleCancelEdit = () => {
    setProfileData({
      name: originalProfileData.name,
      email: originalProfileData.email,
      image: null,
      password: ''
    });
    setImagePreview(profile.image);
    setIsEditing(false);
  };

  const LoadingSpinner = () => (
    <div className="spinner-border text-primary" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
      <span className="visually-hidden">Loading...</span>
    </div>
  );

  return (
    <div className="container-fluid py-4" style={{ direction: "rtl" }}>
      <div className="row justify-content-center">
        <div className="col-lg-10 col-xl-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="card shadow-lg border-0"
            style={{ borderRadius: '20px', overflow: 'hidden' }}
          >
            {/* Header */}
            <div className="card-header border-0 py-4" style={{ 
              background: 'linear-gradient(135deg, red 0%, var(--main-color) 100%)',
              color: 'white'
            }}>
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>
                  الملف الشخصي
                </h4>
                {!isEditing && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setIsEditing(true)}
                    className="btn btn-outline-light btn-sm px-3"
                    style={{ borderRadius: '10px' }}
                  >
                    <FaEdit className="mx-2" />
                    تعديل
                  </motion.button>
                )}
              </div>
            </div>

            <div className="card-body p-5">
              {/* Profile Image */}
              <div className="text-center mb-4">
                <div className="position-relative d-inline-block">
                  <motion.img
                    whileHover={{ scale: 1.05 }}
                    src={imagePreview || '/user.webp'}
                    alt="Profile"
                    className="rounded-circle shadow"
                    style={{
                      width: '120px',
                      height: '120px',
                      objectFit: 'cover',
                      border: '4px solid #f8f9fa',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                    }}
                  />
                  {isEditing && (
                    <label
                      htmlFor="image-upload"
                      className="position-absolute bottom-0 end-0 text-white rounded-circle p-2 shadow-sm"
                      style={{ 
                        cursor: 'pointer', 
                        fontSize: '16px',
                        height: "35px",
                        width: "35px",
                        lineHeight: "0.4em",
                        background: 'linear-gradient(135deg, var(--main-color) 0%, red 100%)',
                        border: '2px solid white'
                      }}
                    >
                      <FaCamera />
                    </label>
                  )}
                  <input
                    type="file"
                    id="image-upload"
                    name="image"
                    accept="image/*"
                    onChange={handleProfileChange}
                    className="d-none"
                    disabled={!isEditing}
                  />
                </div>
              </div>

              {/* Profile Form */}
              <form onSubmit={handleProfileSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold mb-2" style={{ color: '#000', display: "block", textAlign: "right" }}>الاسم</label>
                    <div className="input-group" style={{direction:"ltr"}}>
                      <span className="input-group-text border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa', borderRadius: '12px 0 0 12px' }}>
                        <FaUser className="text-secondary" />
                      </span>
                      <input
                        type="text"
                        name="name"
                        value={profileData.name}
                        onChange={handleProfileChange}
                        className="form-control border-0 shadow-sm"
                        style={{ borderRadius: '0 12px 12px 0', padding: '12px 16px', backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
                        placeholder="أدخل اسمك"
                        required
                        readOnly={!isEditing}
                      />
                    </div>
                  </div>

                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold mb-2" style={{ color: '#000', display: "block", textAlign: "right" }}>البريد الإلكتروني</label>
                    <div className="input-group" style={{direction:"ltr"}}>
                      <span className="input-group-text border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa', borderRadius: '12px 0 0 12px' }}>
                        <FaEnvelope className="text-secondary" />
                      </span>
                      <input
                        type="email"
                        name="email"
                        value={profileData.email}
                        onChange={handleProfileChange}
                        className="form-control border-0 shadow-sm"
                        style={{ borderRadius: '0 12px 12px 0', padding: '12px 16px', backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
                        placeholder="أدخل بريدك الإلكتروني"
                        required
                        readOnly={!isEditing}
                      />
                    </div>
                  </div>
                </div>

                {/* Password (edit mode only) */}
                {isEditing && (
                  <div className="row">
                    <div className="col-md-12 mb-4">
                      <label className="form-label fw-semibold mb-2" style={{ color: '#000', display: "block", textAlign: "right" }}>كلمة المرور الجديدة</label>
                      <div className="input-group" style={{direction:"ltr"}}>
                        <span className="input-group-text border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa', borderRadius: '12px 0 0 12px' }}>
                          <FaLock className="text-secondary" />
                        </span>
                        <input
                          type="password"
                          name="password"
                          value={profileData.password}
                          onChange={handleProfileChange}
                          className="form-control border-0 shadow-sm"
                          style={{ borderRadius: '0 12px 12px 0', padding: '12px 16px', backgroundColor: isEditing ? 'white' : '#f8f9fa' }}
                          placeholder="أدخل كلمة المرور الجديدة"
                          minLength={8}
                          readOnly={!isEditing}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                {isEditing && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="d-flex gap-3 justify-content-center mt-4"
                  >
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      className="btn text-white px-5 py-2 fw-semibold shadow-sm"
                      style={{
                        background: hasChanges 
                          ? 'linear-gradient(135deg, var(--main-color) 0%, red 100%)'
                          : 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '0.95rem',
                        transition: 'all 0.3s ease'
                      }}
                      disabled={loading || !hasChanges}
                    >
                      {loading ? <LoadingSpinner /> : (
                        <>
                          <FaCheck className="me-2" style={{ marginLeft: 4 }} />
                          حفظ التغييرات
                        </>
                      )}
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="button"
                      onClick={handleCancelEdit}
                      className="btn btn-outline-secondary px-5 py-2 fw-semibold shadow-sm"
                      style={{ borderRadius: '12px', fontSize: '0.95rem', borderWidth: '2px' }}
                      disabled={loading}
                    >
                      <FaTimes className="me-2" style={{ marginLeft: 4 }} />
                      إلغاء
                    </motion.button>
                  </motion.div>
                )}
              </form>

              {/* User Info Display */}
              {profile && !isEditing && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-5 pt-4 border-top"
                  style={{ borderColor: '#e9ecef !important' }}
                >
                  <h5 className="mb-4 fw-semibold" style={{ color: '#000', fontSize: '1.3rem', textAlign: "right" }}>
                    معلومات الحساب
                  </h5>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="p-3 rounded-3 shadow-sm" style={{ backgroundColor: '#f8f9fa', textAlign: "right" }}>
                        <strong className="text-muted d-block mb-1">الدور:</strong>
                        <span className="fw-medium" style={{ color: 'var(--main-color)' }}>
                          {profile.role_name || 'غير محدد'}
                        </span>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="p-3 rounded-3 shadow-sm" style={{ backgroundColor: '#f8f9fa', textAlign: "right" }}>
                        <strong className="text-muted d-block mb-1">الصلاحيات:</strong>
                        <span className="fw-medium" style={{ color: 'var(--main-color)' }}>
                          {profile.permissions?.length || 0} صلاحية
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
